import React, { useEffect, useState, useCallback, useImperativeHandle, forwardRef } from "react";
import { createPortal } from "react-dom";  // PHOTO_RATIO_V1: 슬라이드를 body 로 꺼내기
import { listPhotos, deletePhoto, setCoverFromPhoto, uploadPropertyPhoto, savePhotoOrder } from "../api/photoApi";
import { enqueuePhotos, getPhotoCapacity, MAX_PER_SELECT } from "../utils/photoUploadQueue"; // PHOTO_QUEUE_V1: 백그라운드 업로드

// Drive fileId 추출
function extractId(u = "") {
  const m1 = u.match(/[?&]id=([A-Za-z0-9_\-]+)/);
  const m2 = u.match(/\/file\/d\/([A-Za-z0-9_\-]+)/);
  return (m1 && m1[1]) || (m2 && m2[1]) || null;
}
// 프록시 썸네일 URL 생성
// 프록시 썸네일 URL 생성 (기본 크기를 w600 -> w400으로 하향 조정)
function proxyThumb(p = {}, sz = "w200") {
  const id = p.thumb_file_id || extractId(p.thumb_url || p.url || "");
  // 구글 드라이브 직접 URL인 경우 sz 파라미터 조절
  let url = p.thumb_url || p.url || "";
  if (url.includes("drive.google.com") && url.includes("sz=")) {
    return url.replace(/sz=[^&]+/, `sz=${sz}`);
  }
  return id ? `/api/photo-storage/${id}/preview?sz=${sz}` : (url || "/images/no-photo.png");
}

const PhotoGallery = forwardRef(({ propertyId, onCoverChanged, onPhotosLoaded, basicInfo, mode, canEdit = true }, ref) => {
    const [photos, setPhotos] = useState([]);
    const [pendingFiles, setPendingFiles] = useState([]); // 복사 모드용 임시 대기열
    const [idx, setIdx] = useState(-1); // -1=슬라이더 닫힘
    const [hiReady, setHiReady] = useState(-1); // PHOTO_QUEUE_V1: 슬라이드 1200px 로드가 끝난 idx
    const [busy, setBusy] = useState(false);
    const [dragId, setDragId] = useState(null);
    const [ver, setVer] = useState(0); // 이미지 캐시버스터
    const [hoverId, setHoverId] = useState(null);          // 삭제 버튼 hover 제어
    const [err, setErr] = useState("");
    // const bust = (u) => u ? `${u}${u.includes('?') ? '&' : '?'}v=${Date.now()}` : u;
    const waitImage = (url) => new Promise((resolve)=>{ const im = new Image(); im.onload=resolve; im.onerror=resolve; im.src=url; });
    const load = useCallback(async () => {
      // 🔧 신규 등록 화면에는 매물번호가 아직 없음 → 오류가 아니라 정상 상태
      //   기존에는 "no propertyId" 개발용 문구가 빨간 글씨로 노출됐음
      if (!propertyId) {
        setErr("");        // 오류 표시 지움
        setPhotos([]);     // 이전 매물 사진이 남지 않도록 비움
        return;
      }
      setBusy(true);
      try { 
        const ps = await listPhotos(propertyId);
        setPhotos(ps);
        
        setVer(Date.now());

      } finally { setBusy(false); }
    }, [propertyId]); // 의존성에서 onPhotosLoaded 제거

    // 사진 데이터가 로드되어 '상태'가 변했을 때 딱 한 번만 부모에게 보고
    useEffect(() => {
      if (photos.length > 0 && onPhotosLoaded) {
        onPhotosLoaded(photos);
      }
    }, [photos]); // photos 배열이 바뀔 때만 실행

    // ★ 부모(EstateEditor)가 이 함수를 강제로 호출할 수 있게 공개
    useImperativeHandle(ref, () => ({
      uploadPendingFiles: async (newId) => {
        if (pendingFiles.length === 0) return;
        await executeUpload(newId, pendingFiles);
        setPendingFiles([]); // 전송 후 비우기
      }
    }));

    useEffect(() => { setIdx(-1); load(); }, [load]);

    // [PHOTO_QUEUE_V1] 백그라운드 업로드 진행·완료 → 이 매물 갤러리가 열려 있으면 갱신
    //   (창을 닫았다면 utils/photoUploadQueue.js 가 photo:cover / photo:changed 로 목록 썸네일만 갱신)
    useEffect(() => {
      if (!propertyId) return;
      const same = (e) => String(e?.detail?.estateNo) === String(propertyId);
      const onProgress = async (e) => {
        if (!same(e)) return;
        try { setPhotos(await listPhotos(propertyId)); setVer(Date.now()); } catch {}
      };
      const onDone = async (e) => {
        if (!same(e)) return;
        try {
          const ps = await listPhotos(propertyId);
          setPhotos(ps);            // → 위 useEffect 가 onPhotosLoaded(ps) 호출
          setVer(Date.now());
          if (ps[0] && onCoverChanged) onCoverChanged(proxyThumb(ps[0], "w600"));
        } catch {}
      };
      window.addEventListener("photo:queue-progress", onProgress);
      window.addEventListener("photo:queue-done", onDone);
      return () => {
        window.removeEventListener("photo:queue-progress", onProgress);
        window.removeEventListener("photo:queue-done", onDone);
      };
    }, [propertyId, onCoverChanged]);

    // [PHOTO_QUEUE_V1] 슬라이드: ← → Esc 키 + 앞뒤 사진(1200px) 미리 받아두기
    //   capture 단계에서 처리하고 전파를 막음 → Esc 로 매물창까지 닫히지 않게
    useEffect(() => {
      if (idx < 0) return;
      const onKey = (e) => {
        if (e.key === "Escape") setIdx(-1);
        else if (e.key === "ArrowLeft") setIdx(v => Math.max(0, v - 1));
        else if (e.key === "ArrowRight") setIdx(v => Math.min(photos.length - 1, v + 1));
        else return;
        e.preventDefault();
        e.stopPropagation();
      };
      window.addEventListener("keydown", onKey, true);
      [idx - 1, idx + 1].forEach((k) => {
        if (photos[k]) { const im = new Image(); im.src = proxyThumb(photos[k], "w1200"); }
      });
      return () => window.removeEventListener("keydown", onKey, true);
    }, [idx, photos]);

    // 1. 실제 업로드 로직 (통합된 버전)
    // [PHOTO_QUEUE_V1] 파일명 만드는 규칙(newName)은 예전 그대로 두고, 전송만 백그라운드 큐로 넘긴다.
    //   - utils/photoUploadQueue.js 가 1장씩 서버에 전달 (1장 수십 ms)
    //   - 서버(backend services/photoQueue.js)가 구글드라이브 업로드·DB 저장·대표사진 정리
    //   - 매물창을 닫아도 계속됨. 진행은 화면 우측 하단 표시
    //   - 끝나면 'photo:queue-done' 이벤트 → 아래 useEffect 가 갤러리·대표사진 화면 갱신
    //   예전 방식(사진 1장씩 await uploadPropertyPhoto)은 이 블록 교체 전 백업 파일에 있음
    const executeUpload = async (targetId, filesToUpload) => {
      setErr("");
      try {
        let list = Array.from(filesToUpload || []);
        if (!list.length) return;

        // ① 한 번에 최대 50장
        if (list.length > MAX_PER_SELECT) {
          if (!window.confirm(`한 번에 최대 ${MAX_PER_SELECT}장까지 올릴 수 있습니다.\n선택한 ${list.length}장 중 앞의 ${MAX_PER_SELECT}장만 올릴까요?`)) return;
          list = list.slice(0, MAX_PER_SELECT);
        }

        // ② 매물당 최대 50장 (서버가 최종 검사하지만, 미리 알려주기)
        const cap = await getPhotoCapacity(targetId);
        if (cap) {
          if (cap.remaining <= 0) {
            alert(`이 매물은 사진이 이미 ${cap.photos + cap.active}장입니다.\n매물당 최대 ${cap.max}장까지 등록할 수 있습니다.`);
            return;
          }
          if (list.length > cap.remaining) {
            if (!window.confirm(`이 매물은 ${cap.remaining}장 더 올릴 수 있습니다. (현재 ${cap.photos + cap.active}장 / 최대 ${cap.max}장)\n앞의 ${cap.remaining}장만 올릴까요?`)) return;
            list = list.slice(0, cap.remaining);
          }
        }

        // ③ 파일명 — 예전 코드와 동일한 규칙 (서버는 끝의 _순번 만 사용)
        const { roadAddress, jibunAddress, estate_name, estate_ho, floor, floor_etc, floor_type2, floor_type1 } = basicInfo || {};
        const address = roadAddress || jibunAddress || "소재지미정";
        const bldName = estate_name ? ` ${estate_name}` : "";
        let unitPart = estate_ho ? `${estate_ho}호` : (floor_etc || floor_type2 || floor_type1 || (floor ? `${floor}층` : "전체"));
        const today = new Date().toISOString().split('T')[0];

        const renamed = list.map((f, i) => {
          const ext = f.name.split('.').pop();
          const sequence = String(i + 1).padStart(2, '0');
          const newName = `${targetId}_${address}${bldName}_${unitPart}_${today}_${sequence}.${ext}`;
          return new File([f], newName, { type: f.type });
        });

        // ④ 백그라운드 큐에 넘기고 바로 끝 (기다리지 않음)
        enqueuePhotos(targetId, renamed, { label: `${targetId} ${jibunAddress || roadAddress || ""}${bldName} ${unitPart}`.trim() });
      } catch (ex) {
        console.error(ex);
        setErr(String(ex.message || ex));
      }
    };

    // 2. 업로드 이벤트 핸들러
    async function onUpload(e) {
      const files = Array.from(e.target.files || []);
      if (!files.length) return;
      
      console.log("현재 모드:", mode); // 'copy'가 찍히는지 확인

      if (mode === "copy") {
        setPendingFiles(prev => [...prev, ...files]);
        e.target.value = "";
        return;
      }

      // 🔒 [방어] 매물번호 없이 업로드하면 /api/properties/null/photos 가 되어
      //   DB 오류(Incorrect integer value: 'null')가 발생하던 문제
      if (!propertyId) {
        alert("사진은 매물을 먼저 저장한 뒤 등록할 수 있습니다.\n저장 후 상세보기에서 추가해 주세요.");
        e.target.value = "";
        return;
      }

      await executeUpload(propertyId, files);
      e.target.value = "";
    }

  async function onDelete(p, e) {
    e?.stopPropagation?.();
    // [PHOTO_SPLIT_V1] 운영서버 사진은 NAS 에서 지울 수 없음 (운영 사진 사본을 보여주는 것뿐)
    if (p.readonly) { alert("운영서버에 등록된 사진입니다.\nNAS 에서는 삭제할 수 없습니다."); return; }
    if (!window.confirm("삭제합니까?")) return;
    setBusy(true);
    try {
      // 1) DB에서 사진 삭제
      await deletePhoto(propertyId, p.id);

      // 2) 최신 목록 재조회로 내부 갤러리 갱신
      const ps = await listPhotos(propertyId);
      setPhotos(ps);
      setVer(Date.now());

      // [PHOTO_SPLIT_V1] 운영 사진이 섞인 목록(NAS)이면 서버가 대표사진을 운영 첫 사진으로 유지함
      //   → 아래 3)·4) 의 "빈칸 → 재계산"을 하면 NAS 사진이나 빈 사진으로 바뀌므로 건너뜀
      if (ps.some((x) => x.readonly)) {
        const u = ps[0] ? proxyThumb(ps[0], "w600") : "";
        if (onCoverChanged) onCoverChanged(u);
        window.dispatchEvent(new CustomEvent("photo:cover", { detail: { estateNo: propertyId, url: u } }));
        window.dispatchEvent(new Event("photo:changed"));
        return;   // finally 에서 setBusy(false)
      }

      // 3) 화면 즉시 반영: 썸네일을 즉시 no_img로 전환(낙관적 업데이트)
      //    리스트는 normalizeThumb("") -> FALLBACK_NOIMG 로 렌더됨
      if (onCoverChanged) onCoverChanged("");
      window.dispatchEvent(new CustomEvent("photo:cover", { detail: { estateNo: propertyId, url: "" } }));
      window.dispatchEvent(new Event("photo:changed"));

      // 4) 백그라운드로 대표이미지 재계산(남아있으면 최신으로 변경)
      try {
        const r = await fetch(`/api/properties/${propertyId}/thumbnail/recalc`, { method: "PUT" });
        const j = await r.json().catch(()=>({}));
        if (j && typeof j.thumb_url !== "undefined") {
          const nextUrl = j.thumb_url || "";
          if (onCoverChanged) onCoverChanged(nextUrl);
          window.dispatchEvent(new CustomEvent("photo:cover", { detail: { estateNo: propertyId, url: nextUrl } }));
          window.dispatchEvent(new Event("photo:changed"));
        }
      } catch (_) { /* 재계산 실패는 무시. 이미 no_img로 표시됨 */ }
    } catch (ex) {
      console.error(ex);
    } finally {
      setBusy(false);
    }
  }

  async function onCover(p, e) {
    e?.stopPropagation?.();
    setBusy(true);
    try { 
      await setCoverFromPhoto(propertyId, p.id);
      const u = proxyThumb(p, "w600");
      await waitImage(u);
        // 리스트 즉시 반영
        onCoverChanged && onCoverChanged(u);
        window.dispatchEvent(new CustomEvent("photo:cover", { detail: { estateNo: propertyId, url: u } }));
        window.dispatchEvent(new Event("photo:changed"));
    } finally { setBusy(false); }
  }

  // 드래그 드롭 핸들러 분리 (JSX 밖에서 try/finally 안전)
  async function handleDrop(e, i, p) {
    e.preventDefault();
    if (dragId==null || dragId===p.id) return;
    // [PHOTO_SPLIT_V1] 운영 사진은 끌 수도, 그 자리에 놓을 수도 없음 (항상 맨 앞 고정)
    const dragged = photos.find((x) => x.id === dragId);
    if (p.readonly || dragged?.readonly) { setDragId(null); return; }
    const srcIdx = photos.findIndex(x=>x.id===dragId);
    const dstIdx = i;
    if (srcIdx<0 || dstIdx<0) return;
    const next = photos.slice();
    const [moved] = next.splice(srcIdx, 1);
    next.splice(dstIdx, 0, moved);
    setPhotos(next);
    try {
      // [PHOTO_SPLIT_V1] 운영 사진(readonly)은 순서 저장 대상에서 제외, 자리 번호는 전체 목록 기준 유지
      const items = next.map((x, idx)=>({ photoId: x.id, sort_no: (idx+1)*100, ro: x.readonly }))
                        .filter((x) => !x.ro).map(({ photoId, sort_no }) => ({ photoId, sort_no }));
      await savePhotoOrder(propertyId, items);
      const first = next[0];
      if (first && !first.readonly) {   // 운영 사진이 맨 앞이면 대표사진은 운영 것 유지
        await setCoverFromPhoto(propertyId, first.id);
        const u = proxyThumb(first, "w600");
        await waitImage(u);
        onCoverChanged && onCoverChanged(u);
      }
    } catch (ex) {
      console.error(ex);
    } finally {
      setVer(Date.now());
      setDragId(null);
    }
  }

  return (
    <div className="pg-root" style={{ padding: 8 }} onClick={(e)=>e.stopPropagation()}>
      <div style={{ display:"flex", gap:8, alignItems:"center", marginBottom:8 }}>
        {/* 🔒 사진 업로드: premium/admin 만 (canEdit 은 EstateEditor 에서 전달) */}
        {canEdit && (
        <input
          type="file"
          accept="image/*"
          multiple
          disabled={busy}
          onClick={(e)=>e.stopPropagation()}
          onChange={(e)=>{ e.stopPropagation(); onUpload(e); }}
        />
        )}
        {busy && <span>작업중…</span>}
        {err && <span style={{color:"#d00"}}>{err}</span>}
      </div>

{/* ★ [추가] 대기 중인 파일명 상세 리스트 표시 */}
      {pendingFiles.length > 0 && (
        <div style={{
          margin: '10px 0',
          padding: '10px',
          backgroundColor: '#f1f7ff',
          border: '1px solid #c0d9ff',
          borderRadius: '6px'
        }}>
          <div style={{ color: '#004ecb', fontSize: '12px', fontWeight: 'bold', marginBottom: '6px' }}>
            📎 업로드 대기 목록 ({pendingFiles.length}장)
          </div>
          <div style={{ 
            maxHeight: '120px', 
            overflowY: 'auto', 
            fontSize: '11px', 
            color: '#555',
            padding: '4px'
          }}>
            {pendingFiles.map((file, idx) => (
              <div key={`${file.name}-${idx}`} style={{ 
                display: 'flex', 
                justifyContent: 'space-between',
                padding: '2px 0',
                borderBottom: '1px dotted #e0e0e0'
              }}>
                <span>{file.name}</span>
                <span style={{ color: '#999' }}>({(file.size / 1024).toFixed(1)} KB)</span>
              </div>
            ))}
          </div>
          <div style={{ color: 'blue', fontSize: '11px', marginTop: '6px', fontWeight: 'bold' }}>
            ※ [저장] 버튼을 클릭하면 실제 등록이 완료됩니다.
          </div>
        </div>
      )}

      <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fill, 120px)", gap:8 }}>
        {photos.map((p,i)=>(
          <div
            key={p.id}
            style={{ border:"1px solid #eee", borderRadius:6, padding:6, background:"#fff", position:"relative" }}
            onMouseEnter={()=>setHoverId(p.id)}
            onMouseLeave={()=>setHoverId(null)}
            draggable={!p.readonly}   /* [PHOTO_SPLIT_V1] 운영 사진은 끌기 불가 */
            onDragStart={()=>{ if (!p.readonly) setDragId(p.id); }}
            onDragOver={(e)=>e.preventDefault()}
            onDrop={(e)=>handleDrop(e, i, p)}
          >
            <div style={{ position:"relative" }}>
              <img
                src={proxyThumb(p, "w600")}
                alt=""
                loading="lazy" // ★ 그리드 목록 이미지 지연 로딩 추가
                decoding="async" // ★ 비동기 디코딩 추가로 렌더링 성능 향상
                onLoad={(e) => {
                  // ★ 로드 완료 시 부모의 스켈레톤 효과 제거
                  e.currentTarget.parentElement.classList.remove('skeleton-box');
                }}
                style={{
                  width: "100%",
                  height: "auto",
                  aspectRatio: "1 / 1",
                  objectFit: "contain",
                  borderRadius: 4,
                  cursor: "pointer",
                  background: "#000"
                }}
                onClick={() => setIdx(i)}
              />
              {/* 삭제버튼: 이미지 위 오버레이, 호버시에만 보임 */}
              {/* [PHOTO_SPLIT_V1] 운영 사진(NAS 에서 보이는 사본)은 삭제 버튼 대신 표시만 */}
              {p.readonly ? (
                <span
                  title="운영서버 사진 (NAS 에서는 보기만 가능)"
                  style={{ position:"absolute", top:6, left:6, padding:"1px 6px", fontSize:11,
                           borderRadius:4, background:"rgba(30,64,175,0.85)", color:"#fff" }}
                >🔒 운영</span>
              ) : (
              <button
                type="button"
                onClick={(e)=>onDelete(p,e)}
                disabled={busy}
                style={{
                  position:"absolute",
                  top:6, right:6,
                  padding:"4px 8px",
                  fontSize:12,
                  border:"1px solid rgba(0,0,0,0.2)",
                  borderRadius:4,
                  background:"rgba(255,255,255,0.9)",
                  cursor:"pointer",
                  opacity: hoverId===p.id ? 1 : 0,
                  transition:"opacity .15s"
                }}
              >삭제</button>
              )}
            </div>
            {/* ID 출력 제거 */}
            {/* <div style={{ fontSize:11, color:"#777", marginTop:4 }}>id {p.id}</div> */}
          </div>
        ))}
      </div>

      {/* 간단 슬라이더 오버레이 */}
      {idx>=0 && createPortal((
        /* PHOTO_RATIO_V1: 조상(매물창)의 transform 이 fixed 를 가두므로
           이 오버레이만 document.body 에 직접 그린다. 창 이동 기능은 그대로 둔다. */
        <div style={{
          position:"fixed", inset:0, background:"rgba(0,0,0,0.8)",
          display:"flex", alignItems:"center", justifyContent:"center",
          zIndex:2147483000
        }}
        onClick={()=>setIdx(-1)}
        >
          <button onClick={(e)=>{ e.stopPropagation(); setIdx(-1); }} style={{ position:"absolute", top:16, right:20, fontSize:20, color:"#fff", zIndex:2 }}>✕</button>
          <button onClick={(e)=>{ e.stopPropagation(); setIdx(v=>Math.max(0, v-1)); }} style={{ position:"absolute", left:20, color:"#fff", zIndex:2 }}>◀</button>
          {/* [PHOTO_QUEUE_V1] 격자에서 이미 받아둔 600px 를 먼저 보여주고, 1200px 가 도착하면 위에 겹쳐 교체
               → 넘길 때 빈 화면 없이 바로 보임. 두 장 모두 같은 상자에 contain 이라 크기가 튀지 않음 */}
          <div style={{ position:"relative", width:"90vw", height:"85vh" }} onClick={(e)=>e.stopPropagation()}>
            <img
              key={"lo" + idx}
              src={proxyThumb(photos[idx] || {}, "w600")}
              alt=""
              decoding="async"
              style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"contain" }}
            />
            <img
              key={"hi" + idx}
              src={proxyThumb(photos[idx] || {}, "w1200")}
              alt=""
              decoding="async" // ★ 대용량 원본 이미지 디코딩 시 메인 스레드 점유 방지
              onLoad={() => setHiReady(idx)}
              style={{ position:"absolute", inset:0, width:"100%", height:"100%", objectFit:"contain",
                       opacity: hiReady === idx ? 1 : 0, transition:"opacity .15s" }}
            />
          </div>
          <div style={{ position:"absolute", top:20, left:"50%", transform:"translateX(-50%)", color:"#fff", fontSize:14, zIndex:2 }}>
            {idx + 1} / {photos.length}
          </div>
          <button onClick={(e)=>{ e.stopPropagation(); setIdx(v=>Math.min(photos.length-1, v+1)); }} style={{ position:"absolute", right:20, color:"#fff", zIndex:2 }}>▶</button>
        </div>
      ), document.body)}
    </div>
  );
}); // 여기서 딱 한번 닫힘

export default PhotoGallery;